import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const GROUP_BY_OPTIONS = [
  "Senior Region",
  "Team Leader",
  "Management Group Leader",
  "Senior Management Group Leader",
  "Region Leader",
  "Partner Leader",
  "State",
]
const GROUP_BY_TO_API: Record<string, string> = Object.fromEntries(GROUP_BY_OPTIONS.map((o) => [o, o]))

type TrainingsRow = {
  _id: string
  name: string
  count: number
  rsvp_total?: number
  rsvp_rsvp?: number
  rsvp_attended?: number
  rsvp_not_attended?: number
  raffle_total?: number
  raffle_winners?: number
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
}: {
  value: number
  maxVal: number
  colorClass?: string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  return (
    <div className="flex items-center gap-2 min-w-[70px]">
      <span className="text-sm tabular-nums shrink-0 text-ink">{value.toLocaleString()}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export const WeeklyTrainingsReport = () => {
  const [date, setDate] = useState<string>("YEARTODATE")
  const [dateFrom, setDateFrom] = useState<string>("")
  const [dateTo, setDateTo] = useState<string>("")
  const [groupBy, setGroupBy] = useState<string>("Senior Region")
  const [type, setType] = useState<string>("")
  const [connectType, setConnectType] = useState<string>("")
  const [connectFormat, setConnectFormat] = useState<string>("")
  const [state, setState] = useState<string>("")

  const [filterOptions, setFilterOptions] = useState<{
    types: string[]
    connectTypes: string[]
    connectFormats: string[]
    states: string[]
  }>({ types: [], connectTypes: [], connectFormats: [], states: [] })
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, TrainingsRow[]> | null>(null)

  const groupByApi = groupBy ? (GROUP_BY_TO_API[groupBy] ?? "") : ""

  const queryParams = useMemo(
    () => ({
      date: date || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      groupBy: groupByApi || undefined,
      type: type || undefined,
      connectType: connectType || undefined,
      connectFormat: connectFormat || undefined,
      state: state || undefined,
    }),
    [date, dateFrom, dateTo, groupByApi, type, connectType, connectFormat, state]
  )

  const fetchReport = useCallback(() => {
    if (!groupByApi) return
    setLoading(true)
    service
      .getTrainingsRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
        else setApiData(null)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupByApi])

  const fetchFilterOptions = useCallback(() => {
    service.getTrainingsFilterOptions().then((resp: any) => {
      const d = resp?.data?.data ?? resp?.data ?? {}
      setFilterOptions({
        types: Array.isArray(d.types) ? d.types : [],
        connectTypes: Array.isArray(d.connectTypes) ? d.connectTypes : [],
        connectFormats: Array.isArray(d.connectFormats) ? d.connectFormats : [],
        states: Array.isArray(d.states) ? d.states : [],
      })
    })
  }, [])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupByApi) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupByApi, fetchReport])

  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [] as TrainingsRow[], dataGroupBy: null }
    const keyForCurrent = groupByApi ? `by_${groupByApi}` : null
    const arr = keyForCurrent ? apiData[keyForCurrent] : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupByApi }
    }
    const firstKey = Object.keys(apiData)[0]
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? apiData[firstKey] : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, "") : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupByApi])

  const maxCount = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.count), 1) : 1), [tableRows])
  const maxRsvpTotal = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.rsvp_total ?? 0), 1) : 1),
    [tableRows]
  )
  const maxRsvpRsvp = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.rsvp_rsvp ?? 0), 1) : 1),
    [tableRows]
  )
  const maxRsvpAttended = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.rsvp_attended ?? 0), 1) : 1),
    [tableRows]
  )
  const maxRsvpNotAttended = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.rsvp_not_attended ?? 0), 1) : 1),
    [tableRows]
  )
  const maxRaffleTotal = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.raffle_total ?? 0), 1) : 1),
    [tableRows]
  )
  const maxRaffleWinners = useMemo(
    () => (tableRows.length ? Math.max(...tableRows.map((r) => r.raffle_winners ?? 0), 1) : 1),
    [tableRows]
  )

  const totals = useMemo(
    () => ({
      count: tableRows.reduce((s, r) => s + (r.count ?? 0), 0),
      rsvp_total: tableRows.reduce((s, r) => s + (r.rsvp_total ?? 0), 0),
      rsvp_rsvp: tableRows.reduce((s, r) => s + (r.rsvp_rsvp ?? 0), 0),
      rsvp_attended: tableRows.reduce((s, r) => s + (r.rsvp_attended ?? 0), 0),
      rsvp_not_attended: tableRows.reduce((s, r) => s + (r.rsvp_not_attended ?? 0), 0),
      raffle_total: tableRows.reduce((s, r) => s + (r.raffle_total ?? 0), 0),
      raffle_winners: tableRows.reduce((s, r) => s + (r.raffle_winners ?? 0), 0),
    }),
    [tableRows]
  )

  const colCount = 9

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Weekly Trainings Report</h1>
        <p className="page-subtitle">
          Trainings with RSVP and Raffle counts by group (from Trainings, RSVPs, and Raffles).
        </p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Date range</label>
            <select
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Training date from</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Training date to</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {filterOptions.types.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Connect type</label>
            <select
              value={connectType}
              onChange={(e) => setConnectType(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {filterOptions.connectTypes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Connect format</label>
            <select
              value={connectFormat}
              onChange={(e) => setConnectFormat(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {filterOptions.connectFormats.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">State</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              <option value="">All</option>
              {filterOptions.states.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupByApi || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {dataGroupBy && groupByApi && dataGroupBy !== groupByApi && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupByApi}</strong>.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Weekly Trainings by {groupBy || "Group"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Trainings</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">RSVP Total</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">RSVP</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Attended</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Not-Attended</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Raffle Total</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Raffle Winners</th>
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select group by and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr
                    key={row._id != null ? String(row._id) : `row-${idx}`}
                    className="border-b border-line hover:bg-surface transition-colors"
                  >
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.name ?? row._id ?? "—"}</td>
                    <td className="px-2 py-3">
                      <BarCell value={row.count} maxVal={maxCount} colorClass="bg-blue-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rsvp_total ?? 0} maxVal={maxRsvpTotal} colorClass="bg-sky-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rsvp_rsvp ?? 0} maxVal={maxRsvpRsvp} colorClass="bg-amber-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rsvp_attended ?? 0} maxVal={maxRsvpAttended} colorClass="bg-emerald-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rsvp_not_attended ?? 0} maxVal={maxRsvpNotAttended} colorClass="bg-rose-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.raffle_total ?? 0} maxVal={maxRaffleTotal} colorClass="bg-indigo-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.raffle_winners ?? 0} maxVal={maxRaffleWinners} colorClass="bg-violet-500" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-2 py-3 whitespace-nowrap">Total</td>
                <td className="px-2 py-3">{totals.count.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.rsvp_total.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.rsvp_rsvp.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.rsvp_attended.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.rsvp_not_attended.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.raffle_total.toLocaleString()}</td>
                <td className="px-2 py-3">{totals.raffle_winners.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
